import { useState, useEffect, useMemo } from "react";
import { dirOf } from "../utils/textDir";
import { createPortal } from "react-dom";
import { T, UI, FONT, PILL } from "../constants/tokens";
import { useIsMobile } from "../utils/hooks";
import { fetchCalendarEvents, defaultRange } from "../utils/calendar";
import { computeOverlap, overlapTag } from "../utils/geoEvents";
import OfficialBadge from "./OfficialBadge";
import { dateNumeric } from "../utils/formatDate";
import LTR from "./LTR";
import { Amount } from "./Money";
import Icon from "./Icon";
import EmptyState from "./EmptyState";
import { modalScrim, modalPanel } from "./Modal";
import { FINNHUB_VALUATION_ENABLED, ACADEMY_ENABLED } from "../constants/features";
import { MACRO_CONTEXT, EARNINGS_CONTEXT, HOLIDAY_CONTEXT } from "../constants/calendarContext";
import { openAcademyLesson } from "../utils/academyNav";
import { Rich, Link } from "./AcademyText";
import { Skeleton, Loading } from "./Skeleton";

// ─── Economic calendar — real data only ──────────────────────────────────────
// Sources (2026-09-26): the next report of each holding (Finnhub), four
// scheduled US macro releases (FRED release calendar) and NYSE holidays
// (Finnhub). Since sprint 13, the FOMC's scheduled meetings too, from a fixed
// list checked against the Fed's calendar page (constants/fomc.js). Every event shows its source. No demo data, no forecasts, no
// headlines · a news item is not a scheduled event. Anything a source can't
// provide is simply absent (utils/calendar.js).


const TYPE_META = {
  earnings: { color: UI.categorical[0], label: "דוח רבעוני", icon: "chart" },
  macro:    { color: UI.categorical[1], label: "פרסום מאקרו", icon: "signal" },
  holiday:  { color: UI.categorical[2], label: "ללא מסחר",   icon: "bank" },
};
// What replaced the old "impact" score. That field was assigned in calendar.js
// by hand — earnings high-or-medium, IPOs low, holidays medium, geo high — with
// no source behind any of it, and painted in red and gold, which read as a
// verdict the app is not entitled to make. Overlap with the user's holdings is
// a fact we can actually check, so that is what the badge states now.
//
// Calendar events carry `symbols`; computeOverlap reads `tickers` and
// `sectors`. The shape is adapted here rather than bending either side. Only
// geo rows carry sectors (the classifier assigns them), so an earnings or IPO
// row resolves to a direct hit or to nothing — never to a sector match.
function overlapOf(ev, portfolio, watchlist) {
  return computeOverlap(
    { tickers: ev.symbols || [], sectors: (ev.detail && ev.detail.sectors) || [],
      // The headline decides a direct match: the shown title, and for a geo
      // row the original and Hebrew headlines it came from.
      title: ev.title, headline: ev.detail?.headline, headline_he: ev.detail?.headline_he },
    portfolio, watchlist
  );
}


const WEEK_HE   = ["א׳", "ב׳", "ג׳", "ד׳", "ה׳", "ו׳", "ש׳"];
const MONTHS_HE = ["ינואר", "פברואר", "מרץ", "אפריל", "מאי", "יוני", "יולי", "אוגוסט", "ספטמבר", "אוקטובר", "נובמבר", "דצמבר"];

// The legend and the filter row were two controls stating the same four
// categories, one above the other — the legend named the colours and the chips
// repeated the names without them. They are one control now: the chip carries
// the icon and the colour, so reading the key and using it are the same act,
// and a category can no longer be described in one row and missing from the
// other. "הכל" leads because it is the state the screen opens in.
//
// Labels come from TYPE_META so a category is named once in this file. The old
// filter row said "דוחות"/"הנפקות" where the legend said "דוח רבעוני"/"הנפקה";
// merging them removes that split rather than picking a winner.
const FILTERS = [
  { id: "all", label: "הכל", icon: "list", color: UI.text.secondary },
  ...Object.entries(TYPE_META).map(([id, v]) => ({ id, label: v.label, icon: v.icon, color: v.color })),
];

// Length of the forward window shared by the day strip and the intensity
// chart. Both read the same `stripDays` array, so this is the single place the
// window is defined and the two can never drift apart.
const STRIP_DAYS = 7;

// ─── Intensity-chart visibility threshold ────────────────────────────────────
// FIXED PRODUCT DECISION (David, 2026-08-16). Do not change this value without
// asking him first.
//
// The load chart renders only when at least one day inside the window carries
// CHART_MIN_PEAK events or more. Below that the day strip already answers the
// question — it draws a dot per event — and the chart is a second row saying
// the same thing.
//
// Measured on a real week before the rule existed: 38 events across 30 days but
// only 2 inside the 7-day window, one per day. The chart drew two identical
// full-height bars, both labelled 1, directly under a strip already showing a
// dot on those same two days. Outside earnings season that is the normal week.
//
// The hide is silent by construction: IntensityChart returns null, so there is
// no wrapper, no reserved space and no "not enough data" line. The component is
// simply absent, and the surrounding flex gap closes over it.
const CHART_MIN_PEAK = 3;

// ─── Card rhythm ─────────────────────────────────────────────────────────────
// One set of numbers for every event list, so the vertical rhythm is the same
// whether the cards come from the day timeline, the selected day or the
// upcoming list.
const CARD_GAP     = 12;   // between cards
const CARD_PAD     = 16;   // inside a card
const TITLE_GAP    = 16;   // section heading to the first card
const SUBLINE_GAP  = 6;    // card title to its sub-line

// Local calendar day, not the UTC one. `toISOString()` converts first, so a
// Date built from local midnight — which is what `new Date(y, m, d)` gives —
// lands on the previous day for every viewer east of UTC. In Israel that made
// the strip open on yesterday and shifted every month cell by one.
function isoOf(d) {
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${d.getFullYear()}-${m}-${day}`;
}

function countdown(ds, todayISO) {
  const a = new Date(todayISO + "T00:00:00");
  const b = new Date(ds + "T00:00:00");
  const days = Math.round((b - a) / 86400000);
  if (days < 0) return "עבר";
  if (days === 0) return "היום";
  if (days === 1) return "מחר";
  return `בעוד ${days} ימים`;
}

export default function CalendarTab({ lang = "he", portfolio = [], watchlist = [] }) {
  const isMobile = useIsMobile();
  const today = new Date();
  const todayISO = isoOf(today);

  const [events,  setEvents]  = useState(null);   // null = loading
  const [failed,  setFailed]  = useState(false);
  const [filter,  setFilter]  = useState("all");
  const [viewMonth, setViewMonth] = useState({ y: today.getFullYear(), m: today.getMonth() });
  const [selDay,  setSelDay]  = useState(todayISO);
  const [openEvent, setOpenEvent] = useState(null);
  const [view,    setView]    = useState("timeline");  // "timeline" | "month"

  const userSymbols = useMemo(
    () => portfolio.map(p => p.symbol).filter(Boolean),
    [portfolio]
  );

  const load = () => {
    setFailed(false);
    const { from, to } = defaultRange();
    fetchCalendarEvents({ from, to, userSymbols })
      .then(evts => setEvents(evts))
      .catch(() => { setFailed(true); setEvents([]); });
  };

  const symbolsKey = userSymbols.join(",");
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [symbolsKey]);

  const filtered = useMemo(() => {
    if (!Array.isArray(events)) return [];
    return events.filter(e => filter === "all" || e.type === filter);
  }, [events, filter]);

  const byDay = useMemo(() => {
    const m = {};
    for (const e of filtered) (m[e.date] = m[e.date] || []).push(e);
    return m;
  }, [filtered]);

  // The `e.date !== selDay` clause is a de-duplication, not a filter — do not
  // drop it. The selected-day section above renders every event on selDay, and
  // this list starts at today inclusive, so without it every event on the
  // selected day is drawn twice from the same object. It went unnoticed because
  // the two copies are children of different lists, where React allows a
  // repeated key and warns about nothing.
  //
  // It cannot hide anything: the selected-day section renders exactly when
  // byDay[selDay] is non-empty, which is the same condition under which this
  // clause removes anything. For a past selDay the clause is a no-op, since
  // those dates fail `>= todayISO` here regardless.
  const upcoming = useMemo(() => {
    return [...filtered]
      .filter(e => e.date >= todayISO && e.date !== selDay)
      .sort((a, b) => a.date.localeCompare(b.date));
  }, [filtered, todayISO, selDay]);

  // One week forward from today — the window the day strip scrolls through.
  // Forward-only on purpose: the calendar answers "what is coming", and a past
  // day carries nothing the user can still act on.
  const stripDays = useMemo(() => {
    const out = [];
    for (let i = 0; i < STRIP_DAYS; i++) {
      out.push(isoOf(new Date(today.getFullYear(), today.getMonth(), today.getDate() + i)));
    }
    return out;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [todayISO]);

  // Month grid cells
  const firstDow = new Date(viewMonth.y, viewMonth.m, 1).getDay();
  const daysIn   = new Date(viewMonth.y, viewMonth.m + 1, 0).getDate();
  const cells = [];
  for (let i = 0; i < firstDow; i++) cells.push(null);
  for (let d = 1; d <= daysIn; d++) {
    cells.push(isoOf(new Date(viewMonth.y, viewMonth.m, d)));
  }

  const stepMonth = (delta) => {
    setViewMonth(v => {
      const nm = v.m + delta;
      return { y: v.y + Math.floor(nm / 12), m: ((nm % 12) + 12) % 12 };
    });
  };

  return (
    <div className="si-fade-in" style={{ display: "flex", flexDirection: "column", gap: 12, paddingBottom: 20 }}>

      {/* Header */}
      <div style={{ background: UI.surface.raised, border: `1px solid ${UI.line.hairline}`, borderRadius: T.radius.xl, boxShadow: UI.shadow.card, padding: "16px 18px" }}>
        <div style={{ fontSize: isMobile ? 18 : 21, fontWeight: 700, color: UI.text.primary, fontFamily: FONT.heading }}>
          יומן כלכלי
        </div>
        <div style={{ fontSize: 13, color: UI.text.secondary, marginTop: 4 }}>
          דוחות של החזקות, פרסומי מאקרו, ישיבות הפד וימי מסחר
        </div>
      </div>

      {/* Legend and filter, one control */}
      <LegendFilter filter={filter} onChange={setFilter}/>

      {/* View switch — timeline or month grid */}
      <ViewToggle view={view} onChange={setView} label="תצוגה"/>

      {/* Day strip + intensity — timeline view. Both read the same `stripDays`
          window, so the chart is a second view of the strip rather than a
          separate time scale, and either one can pick the day. */}
      {view === "timeline" && (
        <>
          <DayStrip days={stripDays} byDay={byDay} selDay={selDay} todayISO={todayISO} onSelect={setSelDay}/>
          <IntensityChart days={stripDays} byDay={byDay} selDay={selDay} onSelect={setSelDay}/>
        </>
      )}

      {/* Month grid */}
      {view === "month" && (
      <div style={{ background: UI.surface.raised, border: `1px solid ${UI.line.hairline}`, borderRadius: T.radius.xl, boxShadow: UI.shadow.card, padding: "14px" }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 12 }}>
          <button onClick={() => stepMonth(-1)} style={{ background: "transparent", border: `1px solid ${UI.line.hairline}`, borderRadius: 8, width: 40, height: 40, color: UI.text.secondary, cursor: "pointer", fontSize: 16 }}>›</button>
          <span style={{ fontSize: 15, fontWeight: 700, color: UI.text.primary, fontFamily: FONT.heading }}>
            {MONTHS_HE[viewMonth.m]} <LTR>{viewMonth.y}</LTR>
          </span>
          <button onClick={() => stepMonth(1)} style={{ background: "transparent", border: `1px solid ${UI.line.hairline}`, borderRadius: 8, width: 40, height: 40, color: UI.text.secondary, cursor: "pointer", fontSize: 16 }}>‹</button>
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "repeat(7,1fr)", gap: 4 }}>
          {WEEK_HE.map(d => (
            <div key={d} style={{ textAlign: "center", fontSize: 11, fontWeight: 700, color: UI.text.muted, padding: "2px 0" }}>{d}</div>
          ))}
          {cells.map((ds, i) => {
            if (!ds) return <div key={`e${i}`}/>;
            const evs = byDay[ds] || [];
            const isToday = ds === todayISO;
            const isSel   = ds === selDay;
            const dayNum  = Number(ds.slice(8, 10));
            // Up to 3 dots, one per event, coloured by event type.
            const dots = evs.slice(0, 3);
            return (
              <button key={ds} onClick={() => setSelDay(ds)}
                style={{
                  aspectRatio: "1", minHeight: 38, borderRadius: T.radius.md,
                  background: isSel ? UI.state.surface : "transparent",
                  border: isToday ? `1.5px solid ${UI.line.strong}` : `1px solid ${isSel ? UI.state.surface : "transparent"}`,
                  display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 3,
                  cursor: "pointer", fontFamily: "inherit", padding: 0,
                }}>
                <LTR style={{ fontSize: 13, fontWeight: isToday ? 800 : 500, color: isSel ? UI.state.fg : UI.text.primary, fontFamily: FONT.latin }}>{dayNum}</LTR>
                <span style={{ display: "flex", gap: 2, height: 5 }}>
                  {dots.map((e, j) => (
                    <span key={j} style={{ width: 5, height: 5, borderRadius: "50%", background: TYPE_META[e.type]?.color || UI.text.muted }}/>
                  ))}
                </span>
              </button>
            );
          })}
        </div>
      </div>
      )}

      {/* Selected day events.
          The list sections dropped their own card. A raised wrapper holding
          raised cards cancels itself out — the cards were sunken wells inside a
          card, which is why they read as text on a panel rather than as
          objects. The heading now sits on the page and each event card is the
          only raised surface in the group. */}
      {selDay && (byDay[selDay]?.length > 0) && (
        <div>
          <SectionTitle><LTR>{dateNumeric(selDay)}</LTR></SectionTitle>
          {view === "timeline" ? (
            <DayTimeline
              events={byDay[selDay]}
              onOpen={setOpenEvent}
              overlapFor={ev => overlapOf(ev, portfolio, watchlist)}/>
          ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: CARD_GAP }}>
            {byDay[selDay].map(ev => (
              <EventRow key={ev.id} ev={ev} onOpen={() => setOpenEvent(ev)}
                overlap={overlapOf(ev, portfolio, watchlist)}/>
            ))}
          </div>
          )}
        </div>
      )}

      {/* Upcoming list — open by default */}
      <div>
        <SectionTitle>אירועים קרובים</SectionTitle>
        {events === null ? (
          <Loading style={{ display: "flex", flexDirection: "column", gap: CARD_GAP }}>
            {[1, 2, 3].map(i => <Skeleton key={i} h={92} radius={T.radius.xl}/>)}
          </Loading>
        ) : failed ? (
          <div style={{ textAlign: "center", padding: "16px" }}>
            <div style={{ fontSize: 14, color: UI.text.secondary, marginBottom: 12 }}>לא הצלחנו לטעון כרגע · נסה שוב</div>
            <button onClick={() => { setEvents(null); load(); }} style={{ background: UI.accent.soft, border: `1px solid ${UI.accent.hairline}`, borderRadius: T.radius.md, padding: "10px 22px", minHeight: 44, fontSize: 14, fontWeight: 700, color: UI.accent.base, cursor: "pointer", fontFamily: "inherit" }}>נסה שוב</button>
          </div>
        ) : upcoming.length === 0 ? (
          <EmptyState icon="calendar"
            line="אין אירועים קרובים ב-30 הימים הקרובים במקורות הזמינים"/>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: CARD_GAP }}>
            {upcoming.slice(0, 40).map(ev => (
              <EventRow key={ev.id} ev={ev} onOpen={() => setOpenEvent(ev)}
                countdownLabel={countdown(ev.date, todayISO)}
                overlap={overlapOf(ev, portfolio, watchlist)}/>
            ))}
          </div>
        )}
      </div>

      {/* Event detail */}
      {openEvent && (
        <EventDetail ev={openEvent} onClose={() => setOpenEvent(null)} todayISO={todayISO}
          isMobile={isMobile} lang={lang} overlap={overlapOf(openEvent, portfolio, watchlist)}/>
      )}

    </div>
  );
}

function SectionTitle({ children }) {
  return (
    <div style={{ fontSize: 14, fontWeight: 700, color: UI.text.primary, marginBottom: TITLE_GAP, fontFamily: FONT.heading }}>
      {children}
    </div>
  );
}

// ─── Legend · filter ─────────────────────────────────────────────────────────
// One chip per category, and the chip is both the key and the control. Active
// state fills with the category's own hue at 20% and takes the hue for its
// border and label, so the selected category is stated in the same colour the
// cards below it use. "הכל" is not a category and stays neutral — giving it a
// hue would invent a fifth identity.
//
// 13px minimum and a 44px touch target: the old legend ran 12px text next to a
// 9px dot, which is what made it unreadable on a phone.
function LegendFilter({ filter, onChange }) {
  return (
    <div role="group" aria-label="סינון לפי סוג אירוע"
      style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
      {FILTERS.map(f => {
        const active = filter === f.id;
        const neutral = f.id === "all";
        return (
          <button key={f.id} onClick={() => onChange(f.id)}
            aria-pressed={active}
            style={{
              display: "inline-flex", alignItems: "center", gap: 7,
              padding: "10px 16px", minHeight: 44, borderRadius: 999,
              fontSize: 13, fontWeight: active ? 800 : 600, fontFamily: "inherit",
              cursor: "pointer",
              background: active ? (neutral ? UI.fill.subtle : `${f.color}33`) : "transparent",
              border: `1px solid ${active ? (neutral ? UI.line.strong : f.color) : UI.line.hairline}`,
              color: active ? (neutral ? UI.text.primary : f.color) : UI.text.muted,
            }}>
            <Icon name={f.icon} size={15} color={active ? (neutral ? UI.text.primary : f.color) : UI.text.muted}/>
            {f.label}
          </button>
        );
      })}
    </div>
  );
}

// ─── View switch ─────────────────────────────────────────────────────────────
// The selected view is marked with the neutral state plate — not the gold,
// which VISION reserves for insight, Pro and the one primary action.
//
// The label used to be painted in `state.surface`, the SAME token as the plate
// behind it, so the chosen view rendered as a solid rectangle with invisible
// text: two blank cream blocks beside "ציר זמן" and "לוח שנה". The label is
// `state.fg`, which exists for exactly this and is only ever valid on top of
// `state.surface` (DESIGN.md). Same bug family as the calendar's selected-day
// cell in sprint 1.2 — foreground and background bound to different things.
const VIEWS = [
  { id: "timeline", label: "ציר זמן" },
  { id: "month",    label: "לוח שנה" },
];

function ViewToggle({ view, onChange, options = VIEWS, label }) {
  return (
    <div role="group" aria-label={label} style={{ display: "flex", gap: 4, background: UI.surface.raised, border: `1px solid ${UI.line.hairline}`, borderRadius: 999, padding: 4 }}>
      {options.map(v => {
        const active = view === v.id;
        return (
          <button key={v.id} onClick={() => onChange(v.id)}
            aria-pressed={active}
            style={{
              flex: 1, minHeight: 44, borderRadius: 999, cursor: "pointer", fontFamily: "inherit",
              fontSize: 14, fontWeight: active ? 800 : 600,
              background: active ? UI.state.surface : "transparent",
              border: `1px solid ${active ? UI.state.line : "transparent"}`,
              color: active ? UI.state.fg : UI.text.muted,
            }}>
            {v.label}
          </button>
        );
      })}
    </div>
  );
}

// ─── Day strip ───────────────────────────────────────────────────────────────
// One week of days on one scrolling rail. The rail scrolls inside its own box
// so a 390px screen never scrolls sideways as a page — the row is wider than
// the viewport by design, and only this container moves.
const WEEK_LETTER = ["א׳", "ב׳", "ג׳", "ד׳", "ה׳", "ו׳", "ש׳"];

function DayStrip({ days, byDay, selDay, todayISO, onSelect }) {
  return (
    <div style={{ background: UI.surface.raised, border: `1px solid ${UI.line.hairline}`, borderRadius: T.radius.xl, boxShadow: UI.shadow.card, padding: "10px 4px" }}>
      <div style={{ display: "flex", gap: 4, overflowX: "auto", padding: "0 8px", scrollbarWidth: "none" }}>
        {days.map(ds => {
          const evs = byDay[ds] || [];
          const isToday = ds === todayISO;
          const isSel   = ds === selDay;
          const dayNum  = Number(ds.slice(8, 10));
          const dow     = new Date(`${ds}T12:00:00Z`).getDay();
          return (
            <button key={ds} onClick={() => onSelect(ds)}
              aria-pressed={isSel}
              style={{
                flex: "0 0 auto", width: 52, minHeight: 68, borderRadius: T.radius.md, padding: "6px 0",
                display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 2,
                background: isSel ? UI.state.surface : "transparent",
                border: `1px solid ${isSel ? UI.state.surface : UI.line.hairline}`,
                cursor: "pointer", fontFamily: "inherit",
              }}>
              {/* "היום" replaces the weekday letter rather than stacking above
                  it — two lines of meta over one digit reads as noise. */}
              <span style={{ fontSize: 10, fontWeight: 700, color: isSel ? UI.state.fg : UI.text.muted, lineHeight: 1 }}>
                {isToday ? "היום" : WEEK_LETTER[dow]}
              </span>
              <LTR style={{ fontSize: 17, fontWeight: isSel ? 800 : 600, color: isSel ? UI.state.fg : UI.text.primary, fontFamily: FONT.latin, lineHeight: 1.2 }}>{dayNum}</LTR>
              <span style={{ display: "flex", gap: 2, height: 5 }}>
                {evs.slice(0, 3).map((e, j) => (
                  <span key={j} style={{ width: 5, height: 5, borderRadius: "50%", background: TYPE_META[e.type]?.color || UI.text.muted }}/>
                ))}
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
}

// ─── Event intensity ─────────────────────────────────────────────────────────
// How many events land on each day of the strip window. This is the one thing
// the strip and the month grid cannot say: both cap their dots at three, so a
// day with twelve earnings looks the same as a day with three.
//
// Greys from UI.series, not the event-type hues: this is a scale — more and
// less of the same thing — and VISION reserves the grey ramp for exactly that.
// The type colours answer "which", and would be a category answer to a
// magnitude question. The selected day is the one exception and takes the gold,
// the same emphasis it already carries in the strip and the month grid.
//
// Renders nothing when the whole window is empty: a chart of zeros states a
// comparison that has no content.
function IntensityChart({ days, byDay, selDay, onSelect }) {
  const counts = days.map(ds => (byDay[ds] || []).length);
  const max = Math.max(...counts);
  if (max < CHART_MIN_PEAK) return null;

  const H = 64;
  const MIN_FRAC = 0.2;   // a day with any events never reads as empty

  return (
    <div style={{ background: UI.surface.raised, border: `1px solid ${UI.line.hairline}`, borderRadius: T.radius.xl, boxShadow: UI.shadow.card, padding: "14px" }}>
      <div style={{ fontSize: 13, fontWeight: 700, color: UI.text.primary, marginBottom: 14, fontFamily: FONT.heading }}>
        עומס אירועים · {STRIP_DAYS} הימים הקרובים
      </div>
      <div style={{ display: "flex", alignItems: "flex-end", gap: 2 }}>
        {days.map((ds, i) => {
          const n = counts[i];
          const isSel = ds === selDay;
          // Proportional to the busiest day, but floored so one event is
          // visibly more than none. Without the floor a 1-of-12 day drew a
          // 5px stub that read as empty, which is what made the chart look
          // binary — the shape was honest and the scale was unreadable.
          const frac = n === 0 ? 0 : Math.max(MIN_FRAC, n / max);
          const barH = Math.round(frac * H);
          // Two steps of the grey ramp by share of the busiest day. The count
          // above the bar is what actually carries the number; the ramp only
          // separates a heavy day from a light one at a glance.
          const grey = n / max > 0.5 ? UI.series[1] : UI.series[2];
          return (
            <button key={ds} onClick={() => onSelect(ds)}
              aria-label={`${Number(ds.slice(8, 10))} · ${n} אירועים`}
              aria-pressed={isSel}
              style={{
                flex: 1, minWidth: 0, minHeight: 0, background: "transparent", border: "none",
                padding: 0, cursor: "pointer", fontFamily: "inherit",
                display: "flex", flexDirection: "column", alignItems: "center", gap: 5,
              }}>
              {/* The count is the whole point of the chart: it is the one thing
                  neither the day strip nor the month grid can say, since both
                  cap their dots at three. An empty day prints nothing. */}
              <LTR style={{ fontSize: 11, fontWeight: isSel ? 800 : 600, height: 14, lineHeight: "14px", color: isSel ? UI.state.fg : UI.text.secondary, fontFamily: FONT.latin }}>
                {n > 0 ? n : ""}
              </LTR>
              <span style={{ height: H, display: "flex", alignItems: "flex-end", width: "100%", justifyContent: "center" }}>
                {n === 0 ? (
                  // An empty day is a baseline tick, not a bar — a 3px block
                  // sitting where bars sit still reads as a small quantity.
                  <span style={{ display: "block", width: "72%", maxWidth: 14, height: 1, background: UI.line.strong }}/>
                ) : (
                  <span style={{
                    display: "block", width: "72%", maxWidth: 14, height: barH, borderRadius: 3,
                    background: isSel ? UI.state.surface : grey,
                  }}/>
                )}
              </span>
              <LTR style={{ fontSize: 10, fontWeight: isSel ? 800 : 500, color: isSel ? UI.state.fg : UI.text.muted, fontFamily: FONT.latin }}>
                {Number(ds.slice(8, 10))}
              </LTR>
            </button>
          );
        })}
      </div>
    </div>
  );
}

// ─── Day timeline ────────────────────────────────────────────────────────────
// Three trading segments, not clock times. Only earnings carry a stated time,
// and Finnhub states it as one of three codes — before the open, during the
// session, after the close — never an hour. IPOs and holidays carry no time at
// all, and a geo row's `published_at` is when the article went out, not when
// the event happens; reading it as an event time would put a headline in
// "before the open" because a wire service filed at 07:00. Everything without
// a stated time sits in "במהלך היום" instead of being placed at an invented hour.
const SEGMENTS = [
  { id: "allday", label: "במהלך היום" },
  { id: "bmo",    label: "לפני פתיחה" },
  { id: "dmh",    label: "במהלך המסחר" },
  { id: "amc",    label: "אחרי סגירה" },
];

function segmentOf(ev) {
  return ["bmo", "dmh", "amc"].includes(ev.slot) ? ev.slot : "allday";
}

// The clock anchor is a rendering rule, not a field we expect to be filled.
// `ev.time` holds a segment label ("אחרי סגירה"), a trading state ("ללא מסחר")
// or nothing — never an hour — so this returns null for every event the four
// current sources produce, and the anchor column stays empty. If a source ever
// states a real time, the anchor appears on its own. Nothing is invented to
// fill it in the meantime.
const CLOCK_RE = /^([01]?\d|2[0-3]):[0-5]\d$/;
function clockOf(ev) {
  const t = typeof ev.time === "string" ? ev.time.trim() : "";
  return CLOCK_RE.test(t) ? t : null;
}

// calendar.js builds titles with the category as a prefix — "דוח רבעוני · NVDA",
// "הנפקה · Advasa Holdings, Inc." — which was right when the card had no other
// place to say what the row was. The card now names the category on its own
// line, so the prefix would print it twice. Stripped for display only: the
// event's own `title` is left untouched for every other consumer.
//
// Only a head that actually starts with this event's category label is removed,
// so a geo headline that happens to contain a separator keeps all of its words.
function displayTitle(ev, label) {
  const i = ev.title.indexOf(" · ");
  if (i > 0 && ev.title.slice(0, i).startsWith(label)) {
    const rest = ev.title.slice(i + 3).trim();
    if (rest) return rest;
  }
  return ev.title;
}

function DayTimeline({ events, onOpen, overlapFor }) {
  const groups = SEGMENTS
    .map(seg => ({ seg, items: events.filter(e => segmentOf(e) === seg.id) }))
    .filter(g => g.items.length > 0);

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 18 }}>
      {groups.map(({ seg, items }) => (
        <div key={seg.id}>
          <div style={{ fontSize: 12, fontWeight: 700, color: UI.text.muted, marginBottom: 10 }}>
            {seg.label}
          </div>
          {/* The rail sits on the RTL start edge and is drawn behind the nodes.
              A plain hairline, no glow — the nodes carry the colour. */}
          <div style={{ position: "relative", paddingInlineStart: 24 }}>
            <span aria-hidden style={{
              position: "absolute", insetInlineStart: 5, top: 8, bottom: 8,
              width: 1, background: UI.fill.subtle,
            }}/>
            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              {items.map(ev => {
                const clock = clockOf(ev);
                return (
                  <div key={ev.id} style={{ position: "relative" }}>
                    <span aria-hidden style={{
                      position: "absolute", insetInlineStart: -23, top: 26,
                      width: 9, height: 9, borderRadius: "50%",
                      background: TYPE_META[ev.type]?.color || UI.text.muted,
                      // Punches out of whatever the rail sits on. The list
                      // sections lost their wrapper card, so that is the page.
                      border: `2px solid ${UI.surface.page}`,
                    }}/>
                    {clock && (
                      <LTR style={{ display: "block", fontSize: 11, fontWeight: 700, color: UI.text.muted, fontFamily: FONT.latin, marginBottom: 2 }}>
                        {clock}
                      </LTR>
                    )}
                    <EventRow ev={ev} onOpen={() => onOpen(ev)} overlap={overlapFor(ev)} showTime={false}/>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

// ─── Event card ──────────────────────────────────────────────────────────────
// Three lines at most, and the third only when the data has something to put
// there. The category name leads in the category's own colour so the type is
// readable without consulting a legend; the title is the one prominent string.
//
// The card frame is neutral — a hairline on a sunken well. The previous card
// tinted its whole border with the category hue, which put four competing
// colours down a scrolling list and is a large part of why the screen read as
// flat noise. The colour now appears once per card, in the icon disc.
//
// `showTime` is off inside the day timeline, where the segment is already the
// group heading and repeating it in every row says the same thing twice.
function EventRow({ ev, onOpen, countdownLabel, overlap, showTime = true }) {
  const t = TYPE_META[ev.type] || TYPE_META.macro;
  const tag = visibleTag(overlap);
  const [lit, setLit] = useState(false);

  // Tickers earn their place only when they are not already in the title —
  // an earnings title is "דוח רבעוני · NVDA", and printing NVDA again below it
  // is furniture. Geo rows carry tickers the headline never names, which is
  // exactly when the line is worth showing.
  const extraTickers = (ev.symbols || []).filter(s => s && !ev.title.includes(s));
  // A holiday's `time` is "ללא מסחר" — the same words as its category label,
  // so printing both stutters. Shown only when it adds something.
  const timeLabel = showTime && ev.time && ev.time !== t.label ? ev.time : null;
  const hasSubLine = extraTickers.length > 0 || !!timeLabel || !!countdownLabel || !!ev.official;

  return (
    <button onClick={onOpen}
      onMouseEnter={() => setLit(true)} onMouseLeave={() => setLit(false)}
      onFocus={() => setLit(true)} onBlur={() => setLit(false)}
      style={{
        display: "flex", alignItems: "center", gap: 12, width: "100%",
        padding: CARD_PAD, minHeight: 64,
        // One step up from the page, never a well. The separation is carried by
        // that step and the hairline; the shadow only softens the edge and is
        // deliberately the lightest one in the token set.
        background: lit ? UI.surface.overlay : UI.surface.raised,
        border: `1px solid ${UI.line.hairline}`, borderRadius: T.radius.lg,
        boxShadow: T.shadowCard,
        cursor: "pointer", textAlign: "right", fontFamily: "inherit",
        transition: "background .15s ease",
      }}>
      <span style={{
        width: 36, height: 36, borderRadius: "50%", flexShrink: 0,
        background: `${t.color}26`,   // the category hue at 15%
        display: "flex", alignItems: "center", justifyContent: "center",
      }}>
        <Icon name={t.icon} size={18} color={t.color}/>
      </span>

      {/* Weight ladder: the category label used to be bolder (700) than the
          title it labels (600), which is what flattened the hierarchy. The
          label now sits below the title in every channel — smaller, lighter,
          and in a hue rather than the primary ink. */}
      <div style={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column" }}>
        <span style={{ fontSize: 11, fontWeight: 600, color: t.color, marginBottom: 3 }}>
          {ev.kindLabel || t.label}
        </span>
        <span dir={dirOf(displayTitle(ev, t.label))} style={{ fontSize: 15, fontWeight: 600, color: UI.text.primary, lineHeight: 1.35, overflow: "hidden", textOverflow: "ellipsis" }}>
          {displayTitle(ev, t.label)}
        </span>
        {(hasSubLine || tag) && (
          <span style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", fontSize: 12, color: UI.text.secondary, marginTop: SUBLINE_GAP }}>
            {extraTickers.length > 0 && (
              <LTR style={{ fontFamily: FONT.latin, fontWeight: 600 }}>{extraTickers.slice(0, 3).join(" · ")}</LTR>
            )}
            {timeLabel && <span>{timeLabel}</span>}
            {countdownLabel && <span>{countdownLabel}</span>}
            <OfficialBadge official={!!ev.official}/>
            {tag && <span style={{ ...PILL.base, ...tag.style }}>{tag.label}</span>}
          </span>
        )}
      </div>

      <Icon name="chevronLeft" size={16} color={UI.text.muted}/>
    </button>
  );
}

// An overlap tag is shown only when there is an overlap to state. "ללא חפיפה"
// is the absence of a finding and it landed on nearly every row, spending the
// most prominent slot on the card to report that nothing was found. It is not
// rendered at all now — not greyed out, not reserved space: `visibleTag`
// returns null and the tag simply is not in the layout, so the sub-line closes
// over it.
//
// Only the calendar changes. GeoIntelTab keeps its own "ללא חפיפה" — hiding a
// tag there is a separate call on a screen this task did not touch.
function visibleTag(overlap) {
  if (!overlap || (overlap.level !== "hot" && overlap.level !== "geo")) return null;
  return overlapTag(overlap);
}

// Rendered through a portal to document.body, exactly as GeoIntelTab and
// NewsTab already do. On mobile the tab sits inside App's carousel track, and
// that track carries `will-change: transform` plus a live `translateX` — which
// makes it the containing block for any `position: fixed` descendant. Inset-0
// then resolved against a 500vw-wide, page-tall track: the backdrop covered the
// screen while the sheet itself landed thousands of pixels below the viewport,
// which is why a tap dimmed the screen and showed nothing. The portal moves the
// overlay out of the transformed subtree so `fixed` means the viewport again.
// A bottom sheet on mobile, a centred modal on desktop. Three ways out — the
// X, the backdrop, and Escape — because a sheet that traps the reader is worse
// than no sheet.
function EventDetail({ ev, onClose, isMobile, lang, overlap, todayISO }) {
  const t = TYPE_META[ev.type] || TYPE_META.macro;
  const tag = visibleTag(overlap);

  useEffect(() => {
    document.body.style.overflow = "hidden";
    const onKey = e => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = "";
      window.removeEventListener("keydown", onKey);
    };
  }, [onClose]);

  // Every fact below is printed only when the event actually carries it. The
  // holiday `time` repeats its own category label, so it is dropped there too.
  const timeLabel = ev.time && ev.time !== t.label ? ev.time : null;
  // An earnings row is titled by its symbol, so a ticker chip repeating it adds
  // nothing. Same rule the card uses.
  const tickers   = (ev.symbols || []).filter(s => s && !ev.title.includes(s));

  return createPortal(
    <div dir={lang === "he" ? "rtl" : "ltr"} onClick={onClose}
      role="dialog" aria-modal="true" aria-label={ev.title}
      style={modalScrim(1000, { align: isMobile ? "bottom" : "center", padding: isMobile ? 0 : 16 })}>
      <div onClick={e => e.stopPropagation()}
        style={modalPanel({ variant: isMobile ? "sheet" : "dialog", width: isMobile ? 9999 : 480 })}>

        {/* 1 · Header */}
        <div style={{ display: "flex", alignItems: "flex-start", gap: 14, marginBottom: 16 }}>
          <span style={{
            width: 48, height: 48, borderRadius: "50%", flexShrink: 0,
            background: `${t.color}26`,
            display: "flex", alignItems: "center", justifyContent: "center",
          }}>
            <Icon name={t.icon} size={24} color={t.color}/>
          </span>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", marginBottom: 3 }}>
              <span style={{ fontSize: 12, fontWeight: 700, color: t.color }}>{ev.kindLabel || t.label}</span>
              <OfficialBadge official={!!ev.official}/>
            </div>
            <div dir={dirOf(displayTitle(ev, t.label))} style={{ fontSize: isMobile ? 18 : 20, fontWeight: 800, color: UI.text.primary, lineHeight: 1.3, fontFamily: FONT.heading }}>
              {displayTitle(ev, t.label)}
            </div>
          </div>
          <button onClick={onClose} aria-label="סגור"
            style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", flexShrink: 0, background: UI.surface.page, border: `1px solid ${UI.line.hairline}`, borderRadius: 8, width: 36, height: 36, color: UI.text.secondary, cursor: "pointer" }}>
            <Icon name="x" size={15} color={UI.text.secondary}/>
          </button>
        </div>

        {/* 2 · Facts — each chip appears only if the event carries that field */}
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 16 }}>
          <Fact icon="calendar"><LTR>{dateNumeric(ev.date)}</LTR></Fact>
          <Fact icon="hourglass">{countdown(ev.date, todayISO)}</Fact>
          {timeLabel && <Fact icon="clock">{timeLabel}</Fact>}
          {tickers.length > 0 && (
            <Fact icon="hash"><LTR style={{ fontFamily: FONT.latin }}>{tickers.slice(0, 6).join(" · ")}</LTR></Fact>
          )}
        </div>

        {/* Event-type specific factual detail */}
        {/* Analyst estimates are hidden while FINNHUB_VALUATION_ENABLED is
            false (constants/features.js); the report and its date stay. */}
        {FINNHUB_VALUATION_ENABLED && ev.type === "earnings" && (
          <DetailBox>
            <Row label="רווח למניה צפוי" value={ev.detail.epsEstimate != null ? <Amount value={Number(ev.detail.epsEstimate)} currency="USD" decimals={2} size={null} weight={null}/> : "לא פורסם"}/>
            <Row label="הכנסות צפויות" value={ev.detail.revenueEstimate != null ? <Amount value={ev.detail.revenueEstimate / 1e9} currency="USD" decimals={2} suffix="B" size={null} weight={null}/> : "לא פורסם"}/>
          </DetailBox>
        )}
        {ev.type === "holiday" && (
          <ContextBox sources={HOLIDAY_CONTEXT.sources}>
            <p style={CONTEXT_P}>{ev.detail.tradingHour ? HOLIDAY_CONTEXT.half : HOLIDAY_CONTEXT.closed}</p>
            {ev.detail.tradingHour && <Row label="שעות מסחר" value={ev.detail.tradingHour}/>}
          </ContextBox>
        )}
        {/* What the event is · two or three lines from the lesson that explains
            it, with its source and a way into the lesson (sprint 13, stage 4). */}
        <EventContext ev={ev} onClose={onClose}/>
        {/* 3 · Portfolio context — present only when the overlap is real */}
        <PortfolioContext overlap={overlap} tag={tag}/>

        {/* Israel-time note */}
        {(ev.type === "earnings" || ev.type === "holiday") && (
          <div style={{ fontSize: 12, color: UI.text.muted, lineHeight: 1.6, marginBottom: 10 }}>
            השעות מתייחסות למסחר בארה"ב
          </div>
        )}

        {/* 4 · Source */}
        {ev.source && (
          <div style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12, color: UI.text.muted, marginTop: 4, flexWrap: "wrap" }}>
            <Icon name="link" size={13} color={UI.text.muted}/>
            <span>מקור · </span><bdi dir="auto" style={{ fontFamily: FONT.latin }}>{ev.source}</bdi>
          </div>
        )}

        {/* 5 · Standing disclaimer */}
        <div style={{ fontSize: 11, color: UI.text.muted, textAlign: "center", marginTop: 16 }}>
          מידע בלבד · אינו ייעוץ השקעות
        </div>
      </div>
    </div>,
    document.body
  );
}

function Fact({ icon, children }) {
  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 5, background: UI.surface.sunken, border: `1px solid ${UI.line.hairline}`, borderRadius: 8, padding: "6px 12px", fontSize: 12, fontWeight: 700, color: UI.text.secondary }}>
      <Icon name={icon} size={13} color={UI.text.muted}/>
      {children}
    </span>
  );
}

// The card states which of the user's own symbols the event touches, and by
// which mechanism — a direct holding, a watchlist entry, or a shared sector.
// It renders nothing at all when there is no overlap — an empty context box
// would be a heading with nothing under it, and the absence of the card is now
// the only place the screen says "no overlap", since the tag for that state is
// no longer drawn anywhere.
//
// No weight percentage and no impact estimate. computeOverlap does return a
// weightPct, but a share-of-portfolio number next to an event reads as a claim
// about what the event will do to that share, which is not a fact we hold.
function PortfolioContext({ overlap, tag }) {
  if (!overlap || (overlap.level !== "hot" && overlap.level !== "geo")) return null;
  const { heldSymbols = [], watchedSymbols = [], matchedSector, sectorSymbols = [] } = overlap;

  return (
    <div style={{ background: UI.surface.sunken, border: `1px solid ${UI.line.hairline}`, borderRadius: 10, padding: "12px 14px", marginBottom: 14 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
        <span style={{ fontSize: 13, fontWeight: 800, color: UI.text.primary }}>הקשר לתיק שלך</span>
        {tag && <span style={{ ...PILL.base, ...tag.style }}>{tag.label}</span>}
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
        {heldSymbols.length > 0 && (
          <ContextLine label="החזקות בתיק" symbols={heldSymbols}/>
        )}
        {watchedSymbols.length > 0 && (
          <ContextLine label="ברשימת המעקב" symbols={watchedSymbols}/>
        )}
        {matchedSector && (
          <div style={{ fontSize: 13, color: UI.text.secondary, lineHeight: 1.6 }}>
            סקטור משותף · <span style={{ color: UI.text.primary, fontWeight: 700 }}>{matchedSector}</span>
            {sectorSymbols.length > 0 && (
              <> · דרך <LTR style={{ fontFamily: FONT.latin, fontWeight: 700, color: UI.text.primary }}>{sectorSymbols.slice(0, 6).join(" · ")}</LTR></>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function ContextLine({ label, symbols }) {
  return (
    <div style={{ fontSize: 13, color: UI.text.secondary, lineHeight: 1.6 }}>
      {label} · <LTR style={{ fontFamily: FONT.latin, fontWeight: 700, color: UI.text.primary }}>{symbols.slice(0, 6).join(" · ")}</LTR>
    </div>
  );
}

// ── Context from the Academy ────────────────────────────────────────────────
const CONTEXT_P = { margin: "6px 0", fontSize: 13.5, color: UI.text.secondary, lineHeight: 1.7 };

function ContextBox({ sources, lesson, onClose, children }) {
  return (
    <div style={{ background: UI.surface.sunken, border: `1px solid ${UI.line.hairline}`, borderRadius: 10, padding: "8px 14px 12px", marginBottom: 14 }}>
      {children}
      <div style={{ fontSize: 12, color: UI.text.muted, lineHeight: 1.7, marginTop: 6 }}>
        {sources.map((s, i) => (
          <div key={i}>מקור: <Link href={s.url}>{s.text}</Link>
            {s.checked && <> · רשימה קבועה, נבדקה מול העמוד ב-<LTR>{dateNumeric(s.checked)}</LTR></>}
          </div>
        ))}
      </div>
      {lesson && ACADEMY_ENABLED && (
        <button onClick={() => { onClose(); openAcademyLesson(lesson); }}
          style={{ marginTop: 8, background: "transparent", border: "none", padding: "8px 0", minHeight: 44, fontSize: 13.5, fontWeight: 700, color: UI.interactive, cursor: "pointer", fontFamily: "inherit" }}>
          קרא עוד · שיעור <LTR>{lesson}</LTR> באקדמיה
        </button>
      )}
    </div>
  );
}

function EventContext({ ev, onClose }) {
  const ctx = ev.type === "earnings" ? EARNINGS_CONTEXT
    : ev.type === "macro" ? MACRO_CONTEXT[ev.detail?.key]
    : null;
  if (!ctx) return null;
  const fomc = ev.detail?.key === "fomc";
  return (
    <ContextBox sources={ctx.sources} lesson={ctx.lesson} onClose={onClose}>
      {ctx.lines.map((l, i) => <p key={i} style={CONTEXT_P}><Rich text={l}/></p>)}
      {fomc && ev.detail.start && (
        <p style={CONTEXT_P}>
          ישיבה בת יומיים, <LTR>{dateNumeric(ev.detail.start)}</LTR> עד <LTR>{dateNumeric(ev.date)}</LTR>
          {ev.detail.sep && <>. לפי הפד, הישיבה מלווה ב-<LTR>Summary of Economic Projections</LTR></>}.
        </p>
      )}
    </ContextBox>
  );
}

function DetailBox({ children }) {
  return (
    <div style={{ background: UI.surface.page, border: `1px solid ${UI.line.hairline}`, borderRadius: 10, padding: "6px 14px", marginBottom: 14 }}>
      {children}
    </div>
  );
}
function Row({ label, value }) {
  return (
    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "8px 0", borderBottom: `1px solid ${UI.line.hairline}` }}>
      <span style={{ fontSize: 13, color: UI.text.secondary }}>{label}</span>
      <LTR style={{ fontSize: 14, fontWeight: 700, color: UI.text.primary, fontFamily: FONT.latin }}>{value}</LTR>
    </div>
  );
}
